/**
 * styled-field — wspólny „klocek" renderu pola z bogatego layoutu.
 *
 * Bogaty renderer kartotek (`RecursiveFormRenderer`) potrafi trzy rzeczy, których
 * dedykowane renderery zakładek „Dane" (ContractorDataTab i 4 bliźniaki) nie robiły:
 *  1. **styl pola** — kolor/typografia etykiety (`settings.labelStyle`) i kontrolki
 *     (`settings.style`) przez scoped `<style>` po klasie unikalnej per element,
 *  2. **warunek widoczności** — `settings.conditionalField/Operator/Value` vs `formData`,
 *  3. (wyróżnik/kolory wariantów rozwiązane osobno — tokeny motywu, tailwind.config).
 *
 * Ten moduł wydziela punkty 1–2 do JEDNEGO źródła prawdy, żeby bespoke renderery i
 * `RecursiveFormRenderer` nie rozjechały się. `RecursiveFormRenderer` wpina te same
 * funkcje (`isFieldElementVisible`, `buildFieldScopedCss`), a bespoke tabs owijają
 * wynik `renderField` w `<StyledFieldSlot>`.
 *
 * Dlaczego `display:contents` w `<StyledFieldSlot>`: bespoke tab renderuje pola
 * WEWNĄTRZ gridu sekcji (`CoreFieldLayout.Section` → `Field` = bezpośrednie dziecko
 * gridu z własnym `md:col-span-2`). Zwykły `<div>` wrapper przejąłby rolę komórki
 * gridu i zepsuł col-span. `display:contents` nie generuje boksu — `Field` zostaje
 * efektywną komórką gridu, a klasa `.fld-<id>` i tak jest przodkiem w DOM, więc
 * selektory scoped-CSS (`.fld-<id> label`, `.fld-<id> input`) dalej trafiają.
 */

import { Fragment, type ReactNode } from 'react';

import { isLayoutElementVisible } from '@/components/fields/LayoutElementRegistry';
import type { LayoutElementStyle } from '@/components/module-builder/style-panel';
import {
  getAnimationClass,
  getAnimationStyle,
  styleToCSS,
} from '@/components/module-builder/style-panel';

/** Minimalny kształt elementu-pola, na którym operuje ten moduł. */
export interface StyledFieldElement {
  id: string;
  type?: string;
  fieldId?: string;
  hidden?: boolean;
  settings?: Record<string, unknown>;
  conditionalField?: string | null;
  conditionalOperator?: string | null;
  conditionalValue?: unknown;
}

/** React.CSSProperties → tekst CSS (kebab-case) do scoped `<style>`. */
export function cssText(css: Record<string, string | number | undefined> | undefined): string {
  if (!css) return '';
  return Object.entries(css)
    .filter(([, v]) => v != null && v !== '')
    .map(([k, v]) => `${k.replace(/[A-Z]/g, (m) => '-' + m.toLowerCase())}:${v}`)
    .join(';');
}

/**
 * Czy pole jest widoczne? Warunek bierzemy z pól najwyższego poziomu, a gdy ich brak —
 * z `settings` (builder zapisuje warunek w settings, jedną rurą razem ze stylem/etykietą).
 * Identyczna semantyka jak `RecursiveFormRenderer.isVisible` — teraz jedno źródło prawdy.
 */
export function isFieldElementVisible(
  el: StyledFieldElement,
  formData: Record<string, unknown>,
): boolean {
  const s = (el.settings ?? {}) as {
    conditionalField?: unknown;
    conditionalOperator?: unknown;
    conditionalValue?: unknown;
  };
  const condField =
    el.conditionalField ?? (typeof s.conditionalField === 'string' ? s.conditionalField : null);
  const condOperator =
    el.conditionalOperator ??
    (typeof s.conditionalOperator === 'string' ? s.conditionalOperator : null);
  const condValue = el.conditionalValue ?? s.conditionalValue ?? null;
  return isLayoutElementVisible(
    {
      id: el.id,
      type: el.type ?? 'field',
      settings: el.settings,
      conditionalField: condField,
      conditionalOperator:
        (condOperator as 'equals' | 'not_equals' | 'empty' | 'not_empty' | null) ?? null,
      conditionalValue: condValue != null ? String(condValue) : null,
    },
    formData,
    'form',
  );
}

/**
 * Buduje scoped CSS dla pola: `.fld-<id> label{…}` (etykieta = `settings.labelStyle`)
 * oraz `.fld-<id> input,select,textarea{…}` (kontrolka = `settings.style` + animacja).
 * Zwraca `''`, gdy pole nie ma stylu.
 */
export function buildFieldScopedCss(el: StyledFieldElement): string {
  const fs = el.settings as
    | { style?: LayoutElementStyle; labelStyle?: LayoutElementStyle }
    | undefined;
  const bodyStyle = fs?.style;
  const labelStyle = fs?.labelStyle;
  const cls = `fld-${el.id}`;
  const labelCss = cssText(styleToCSS(labelStyle) as Record<string, string | number | undefined>);
  const bodyCss = cssText({
    ...styleToCSS(bodyStyle),
    ...getAnimationStyle(bodyStyle),
  } as Record<string, string | number | undefined>);
  return [
    labelCss ? `.${cls} label{${labelCss}}` : '',
    bodyCss ? `.${cls} input,.${cls} select,.${cls} textarea{${bodyCss}}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

export interface StyledFieldSlotProps {
  el: StyledFieldElement;
  formData: Record<string, unknown>;
  children: ReactNode;
}

/**
 * Owija `renderField(...)` w bespoke zakładce: honoruje warunek widoczności + `hidden`,
 * nakłada scoped-CSS stylu pola. `display:contents` zachowuje grid sekcji (patrz JSDoc
 * modułu). Gdy pole nie ma ani stylu, ani animacji — przepuszcza dziecko bez wrappera,
 * żeby nie zmieniać DOM tam, gdzie nic nie stylujemy (zero regresji layoutu).
 */
export function StyledFieldSlot({ el, formData, children }: StyledFieldSlotProps): ReactNode {
  if (el.hidden === true) return null;
  if (!isFieldElementVisible(el, formData)) return null;

  const scoped = buildFieldScopedCss(el);
  const animClass = getAnimationClass(
    (el.settings as { style?: LayoutElementStyle } | undefined)?.style,
  );
  if (!scoped && !animClass) return <Fragment>{children}</Fragment>;

  const cls = `fld-${el.id}`;
  return (
    <div style={{ display: 'contents' }} className={animClass ? `${cls} ${animClass}` : cls}>
      {scoped ? <style>{scoped}</style> : null}
      {children}
    </div>
  );
}
